/**
 * Stripe Context Provider
 *
 * Provides Stripe.js instance and Elements provider to the application.
 * Handles lazy loading of Stripe and configuration fetching.
 */

import { createContext, useState, useEffect, ReactNode } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useStripeConfig } from '@/hooks/usePayments';

// =============================================================================
// Types
// =============================================================================

interface StripeContextValue {
    stripe: Stripe | null;
    isLoading: boolean;
    error: string | null;
    publishableKey: string | null;
}

interface StripeProviderProps {
    children: ReactNode;
}

// =============================================================================
// Context
// =============================================================================

const StripeContext = createContext<StripeContextValue>({
    stripe: null,
    isLoading: true,
    error: null,
    publishableKey: null,
});

// NOTE: the old context useStripe() hook was removed — Elements consumers use
// @stripe/react-stripe-js's useStripe() inside StripeElementsWrapper, which owns
// the per-payment Stripe instance (multi-tenant publishable keys).

// =============================================================================
// Provider
// =============================================================================

/**
 * Stripe Provider Component
 *
 * Wraps your app to provide Stripe functionality.
 * Automatically fetches the publishable key from the API and initializes Stripe.
 *
 * Usage:
 * ```tsx
 * <StripeProvider>
 *   <App />
 * </StripeProvider>
 * ```
 */
export const StripeProvider = ({ children }: StripeProviderProps) => {
    // Multi-tenant: the publishable key depends on the booking location's payment profile
    // and is delivered with each PaymentIntent/SetupIntent response — there is no
    // platform-wide key to preload. Eagerly loading Stripe here initialized the whole site
    // against the platform account (and, with test keys configured, showed Stripe's
    // test-mode developer dialog on every page). The provider remains only so legacy
    // useStripe() consumers keep working; Elements are initialized per-payment by
    // StripeElementsWrapper with the response's publishable key.
    const value: StripeContextValue = {
        stripe: null,
        isLoading: false,
        error: null,
        publishableKey: null,
    };

    return (
        <StripeContext.Provider value={value}>
            {children}
        </StripeContext.Provider>
    );
};

// =============================================================================
// Elements Wrapper
// =============================================================================

interface StripeElementsWrapperProps {
    clientSecret: string;
    children: ReactNode;
    publishableKey?: string;
    appearance?: {
        theme?: 'stripe' | 'night' | 'flat';
        variables?: Record<string, string>;
    };
}

const STRIPE_APPEARANCE_DEFAULTS = {
    colorPrimary: '#0F766E',
    colorBackground: '#FFFFFF',
    colorText: '#374151',
    colorTextSecondary: '#68606f',
    colorDanger: '#D9534F',
    colorBorder: '#e0ddd2',
    fontFamily: 'Nunito, ui-sans-serif, system-ui, sans-serif',
    spacingUnit: '4px',
    borderRadius: '10px',
};

const getStripeAppearanceVariables = () => {
    if (typeof window === 'undefined') {
        return STRIPE_APPEARANCE_DEFAULTS;
    }

    const styles = window.getComputedStyle(document.documentElement);

    const readToken = (name: string, fallback: string) => {
        const value = styles.getPropertyValue(name).trim();
        return value ? `hsl(${value})` : fallback;
    };

    return {
        colorPrimary: readToken('--primary', STRIPE_APPEARANCE_DEFAULTS.colorPrimary),
        colorBackground: readToken('--card', STRIPE_APPEARANCE_DEFAULTS.colorBackground),
        colorText: readToken('--foreground', STRIPE_APPEARANCE_DEFAULTS.colorText),
        colorTextSecondary: readToken('--muted-foreground', STRIPE_APPEARANCE_DEFAULTS.colorTextSecondary),
        colorDanger: readToken('--destructive', STRIPE_APPEARANCE_DEFAULTS.colorDanger),
        colorBorder: readToken('--border', STRIPE_APPEARANCE_DEFAULTS.colorBorder),
        fontFamily: STRIPE_APPEARANCE_DEFAULTS.fontFamily,
        spacingUnit: STRIPE_APPEARANCE_DEFAULTS.spacingUnit,
        borderRadius: STRIPE_APPEARANCE_DEFAULTS.borderRadius,
    };
};

/**
 * Wrapper component that provides Stripe Elements with a client secret.
 * Use this to wrap payment forms that need access to Stripe Elements.
 *
 * Usage:
 * ```tsx
 * <StripeElementsWrapper clientSecret={clientSecret}>
 *   <PaymentForm />
 * </StripeElementsWrapper>
 * ```
 */
export const StripeElementsWrapper = ({
    clientSecret,
    children,
    publishableKey,
    appearance,
}: StripeElementsWrapperProps) => {
    // Multi-tenant: prefer the per-payment publishable key (the Stripe account the
    // PaymentIntent/SetupIntent was created on, i.e. the booking location's payment
    // profile). Only when the caller cannot supply one do we fall back to the platform
    // config key — fetched lazily HERE so the platform Stripe instance is never loaded
    // on pages that don't take platform-account payments.
    const { data: config, isLoading: configLoading, error: configError } = useStripeConfig(!publishableKey);
    const effectiveKey = publishableKey ?? config?.publishableKey ?? null;

    const [effectiveStripe, setEffectiveStripe] = useState<Stripe | null>(null);
    const [stripeLoading, setStripeLoading] = useState(true);
    const [stripeError, setStripeError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        const initStripe = async () => {
            if (!effectiveKey) {
                return;
            }

            try {
                if (isMounted) {
                    setStripeLoading(true);
                    setStripeError(null);
                }

                const stripeInstance = await loadStripe(effectiveKey);

                if (isMounted) {
                    setEffectiveStripe(stripeInstance);
                }
            } catch (err) {
                if (isMounted) {
                    setStripeError('Failed to initialize payment system');
                    console.error('Stripe initialization error:', err);
                }
            } finally {
                if (isMounted) {
                    setStripeLoading(false);
                }
            }
        };

        initStripe();

        return () => {
            isMounted = false;
        };
    }, [effectiveKey]);

    const effectiveLoading = effectiveKey ? stripeLoading : configLoading;
    const effectiveError = effectiveKey
        ? stripeError
        : (configError ? 'Failed to load payment configuration' : null);

    if (effectiveLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (effectiveError || !effectiveStripe) {
        return (
            <div className="p-4 bg-destructive-subtle border border-destructive-border rounded-lg text-destructive">
                {effectiveError || 'Payment system unavailable. Please try again later.'}
            </div>
        );
    }

    const options = {
        clientSecret,
        appearance: {
            theme: appearance?.theme || 'stripe' as const,
            variables: {
                ...getStripeAppearanceVariables(),
                ...appearance?.variables,
            },
        },
    };

    return (
        <Elements stripe={effectiveStripe} options={options}>
            {children}
        </Elements>
    );
};

export default StripeProvider;
